import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { 
  Users, 
  Store, 
  Briefcase, 
  Shield, 
  Edit, 
  Trash2, 
  Search,
  CheckCircle,
  XCircle,
  ArrowLeft,
  TrendingUp,
  Upload,
  Download,
  FileSpreadsheet
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [popularProfessionals, setPopularProfessionals] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [loadingPopular, setLoadingPopular] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [csvFile, setCsvFile] = useState(null);
  const [uploadingCSV, setUploadingCSV] = useState(false);

  useEffect(() => {
    // Check if user is admin
    if (!authLoading && user) {
      if (!user.is_admin) {
        toast.error('Admin access required');
        navigate('/');
        return;
      }
      fetchStats();
      fetchUsers();
      fetchVendors();
      fetchPopularProfessionals();
    } else if (!authLoading && !user) {
      toast.error('Please login to access admin panel');
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  const fetchPopularProfessionals = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/analytics/popular-professionals?limit=10`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setPopularProfessionals(data);
      }
    } catch (error) {
      console.error('Error fetching popular professionals:', error);
    } finally {
      setLoadingPopular(false);
    }
  };

  const fetchStats = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/stats`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        toast.error('Failed to fetch stats');
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams({ limit: 100 });
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await fetch(`${backendUrl}/api/admin/users?${params}`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams({ limit: 100 });
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await fetch(`${backendUrl}/api/admin/vendors?${params}`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setVendors(data);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      setLoadingVendors(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (response.ok) {
        toast.success('User deleted successfully');
        fetchUsers();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/vendors/${vendorId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (response.ok) {
        toast.success('Vendor deleted successfully');
        fetchVendors();
        fetchStats();
      } else {
        toast.error('Failed to delete vendor');
      }
    } catch (error) {
      console.error('Error deleting vendor:', error);
      toast.error('Failed to delete vendor');
    }
  };

  const handleToggleVendorVerification = async (vendorId, currentStatus) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(
        `${backendUrl}/api/admin/vendors/${vendorId}/verify?verified=${!currentStatus}`,
        {
          method: 'PATCH',
          credentials: 'include',
        }
      );
      
      if (response.ok) {
        toast.success(`Vendor ${!currentStatus ? 'verified' : 'unverified'} successfully`);
        fetchVendors();
        fetchStats();
      } else {
        toast.error('Failed to update verification status');
      }
    } catch (error) {
      console.error('Error updating vendor:', error);
      toast.error('Failed to update vendor');
    }
  };

  const handleCSVFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setCsvFile(file);
  };

  const handleDownloadTemplate = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/import/vendors/csv/template`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'vendor_import_template.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Template downloaded successfully');
      } else {
        toast.error('Failed to download template');
      }
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const handleUploadCSV = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setUploadingCSV(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const formData = new FormData();
      formData.append('file', csvFile);
      
      const response = await fetch(`${backendUrl}/api/import/vendors/csv`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`✅ Import complete! ${result.imported} vendors imported, ${result.skipped} skipped`);
        if (result.errors && result.errors.length > 0) {
          console.warn('Import errors:', result.errors);
        }
        setCsvFile(null);
        fetchVendors();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to import CSV');
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      toast.error('Failed to upload CSV');
    } finally {
      setUploadingCSV(false);
    }
  };

  if (authLoading || loadingStats) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-accent border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border backdrop-blur-sm bg-background/95 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                className="hover:bg-accent/10 hover:text-accent"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <Shield className="w-8 h-8 text-accent" />
                <div>
                  <h1 className="text-xl font-bold text-accent">Admin Dashboard</h1>
                  <p className="text-xs text-muted-foreground">Shoot Base Management</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <img 
                src={user?.picture || '/logo.png'} 
                alt={user?.name} 
                className="w-10 h-10 rounded-full"
              />
              <div className="text-right hidden md:block">
                <p className="text-sm font-medium">{user?.name}</p>
                <Badge className="bg-accent/10 text-accent">Admin</Badge>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Dashboard Content */}
      <div className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="vendors">Vendors</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-4 gap-6">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <Card className="bg-gradient-to-br from-blue-50 to-background border-blue-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Users</p>
                        <h3 className="text-3xl font-bold mt-2">{stats?.total_users || 0}</h3>
                      </div>
                      <Users className="w-12 h-12 text-blue-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <Card className="bg-gradient-to-br from-orange-50 to-background border-orange-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Vendors</p>
                        <h3 className="text-3xl font-bold mt-2">{stats?.total_vendors || 0}</h3>
                      </div>
                      <Store className="w-12 h-12 text-orange-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                <Card className="bg-gradient-to-br from-green-50 to-background border-green-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Verified Vendors</p>
                        <h3 className="text-3xl font-bold mt-2">{stats?.verified_vendors || 0}</h3>
                      </div>
                      <CheckCircle className="w-12 h-12 text-green-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                <Card className="bg-gradient-to-br from-purple-50 to-background border-purple-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Projects</p>
                        <h3 className="text-3xl font-bold mt-2">{stats?.total_projects || 0}</h3>
                      </div>
                      <Briefcase className="w-12 h-12 text-purple-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            <Card className="mt-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-accent" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <Button onClick={() => setActiveTab('users')} className="bg-accent hover:bg-accent/90">
                    <Users className="w-4 h-4 mr-2" />
                    Manage Users
                  </Button>
                  <Button onClick={() => setActiveTab('vendors')} className="bg-accent hover:bg-accent/90">
                    <Store className="w-4 h-4 mr-2" />
                    Manage Vendors
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Popular Professionals Analytics */}
            <Card className="mt-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-accent" />
                  Most Contacted Professionals
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingPopular ? (
                  <div className="text-center py-8">
                    <div className="animate-spin w-8 h-8 border-4 border-accent border-t-transparent rounded-full mx-auto mb-2"></div>
                    <p className="text-sm text-muted-foreground">Loading analytics...</p>
                  </div>
                ) : popularProfessionals.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No contact data available yet</p>
                ) : (
                  <div className="space-y-3">
                    {popularProfessionals.map((prof, idx) => (
                      <div 
                        key={prof.user_id}
                        className="flex items-center justify-between p-3 rounded-lg border border-border hover:border-accent/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-accent/10 text-accent font-bold text-sm">
                            #{idx + 1}
                          </div>
                          <div>
                            <p className="font-semibold">{prof.name}</p>
                            <p className="text-sm text-muted-foreground">{prof.designation || 'Professional'}</p>
                          </div>
                        </div>
                        <Badge className="bg-accent/10 text-accent border-accent/20">
                          {prof.contact_count} {prof.contact_count === 1 ? 'contact' : 'contacts'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="mt-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-accent" />
                  Bulk Import Vendors
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Import multiple vendors at once using a CSV file. Download the template, fill it with vendor data, and upload.
                </p>
                
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={handleDownloadTemplate}
                      className="flex-1"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download CSV Template
                    </Button>
                  </div>
                  
                  <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                    <Input
                      type="file"
                      accept=".csv"
                      onChange={handleCSVFileSelect}
                      className="hidden"
                      id="csv-upload"
                    />
                    <label
                      htmlFor="csv-upload"
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <Upload className="w-8 h-8 text-muted-foreground" />
                      {csvFile ? (
                        <div className="text-sm">
                          <p className="font-medium text-accent">✓ {csvFile.name}</p>
                          <p className="text-xs text-muted-foreground">Click to change file</p>
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          <p className="font-medium">Click to select CSV file</p>
                          <p className="text-xs">or drag and drop</p>
                        </div>
                      )}
                    </label>
                  </div>
                  
                  {csvFile && (
                    <Button
                      onClick={handleUploadCSV}
                      disabled={uploadingCSV}
                      className="w-full bg-accent hover:bg-accent/90"
                    >
                      {uploadingCSV ? (
                        <>Processing...</>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Import Vendors from CSV
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>User Management</CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && fetchUsers()}
                        className="pl-10 w-64"
                      />
                    </div>
                    <Button size="sm" onClick={fetchUsers}>Search</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingUsers ? (
                  <p className="text-center text-muted-foreground py-8">Loading users...</p>
                ) : users.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No users found</p>
                ) : (
                  <div className="space-y-4">
                    {users.map((u) => (
                      <div
                        key={u.user_id}
                        className="flex items-center justify-between p-4 border border-border rounded-lg hover:border-accent/50 transition-all"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center text-accent font-bold">
                            {u.name?.charAt(0) || 'U'}
                          </div>
                          <div>
                            <h4 className="font-semibold">{u.name}</h4>
                            <p className="text-sm text-muted-foreground">{u.email}</p>
                            {u.designation && (
                              <p className="text-xs text-accent mt-1">{u.designation}</p>
                            )}
                            {u.departments && u.departments.length > 0 && (
                              <div className="flex gap-1 mt-1">
                                {u.departments.slice(0, 2).map(dept => (
                                  <Badge key={dept} variant="secondary" className="text-xs">
                                    {dept.replace(/-/g, ' ')}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {u.is_admin && (
                            <Badge className="bg-accent/10 text-accent">Admin</Badge>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteUser(u.user_id)}
                            disabled={u.is_admin}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vendors Tab */}
          <TabsContent value="vendors">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Vendor Management</CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search vendors..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && fetchVendors()}
                        className="pl-10 w-64"
                      />
                    </div>
                    <Button size="sm" onClick={fetchVendors}>Search</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingVendors ? (
                  <p className="text-center text-muted-foreground py-8">Loading vendors...</p>
                ) : vendors.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No vendors found</p>
                ) : (
                  <div className="space-y-4">
                    {vendors.map((v) => (
                      <div
                        key={v.vendor_id}
                        className="flex items-center justify-between p-4 border border-border rounded-lg hover:border-accent/50 transition-all"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center text-accent font-bold">
                            {v.vendor_name?.charAt(0) || 'V'}
                          </div>
                          <div>
                            <h4 className="font-semibold">{v.vendor_name}</h4>
                            <p className="text-sm text-muted-foreground">{v.category}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {v.city}, {v.state || 'N/A'}
                            </p>
                            {v.services && v.services.length > 0 && (
                              <div className="flex gap-1 mt-1">
                                {v.services.slice(0, 2).map((service, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs">
                                    {service}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant={v.verified ? "default" : "outline"}
                            onClick={() => handleToggleVendorVerification(v.vendor_id, v.verified)}
                            className={v.verified ? "bg-green-600 hover:bg-green-700" : ""}
                          >
                            {v.verified ? (
                              <><CheckCircle className="w-4 h-4 mr-1" />Verified</>
                            ) : (
                              <><XCircle className="w-4 h-4 mr-1" />Verify</>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteVendor(v.vendor_id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
